import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Heart, Brain, Leaf, Bug, Microscope, Dna, 
  ArrowLeft, ArrowRight, Play, RotateCcw, 
  CheckCircle2, Star, Eye, Droplets, Wind,
  Sun, TreeDeciduous, Flower2, Bird, Fish,
  ChevronRight, Sparkles, Volume2, VolumeX,
  Zap, Activity
} from 'lucide-react';

const BiologyLabPage = () => {
  const navigate = useNavigate();
  const [activeExperiment, setActiveExperiment] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Biology Experiments/Simulations
  const experiments = [
    {
      id: 'human-body',
      title: 'Human Body Systems',
      titleHindi: 'मानव शरीर तंत्र',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      description: 'Explore heart, lungs, brain and more!',
      hindiDesc: 'दिल, फेफड़े, दिमाग की खोज करो!',
      level: 'Class 6-8',
      chapters: ['Circulatory System', 'Respiratory System', 'Nervous System', 'Digestive System'],
      simulation: 'heart'
    },
    {
      id: 'plant-life',
      title: 'Plant Kingdom',
      titleHindi: 'पादप जगत',
      icon: Leaf,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      description: 'Photosynthesis, plant parts & life cycle',
      hindiDesc: 'प्रकाश संश्लेषण और पौधों का जीवन',
      level: 'Class 6-9',
      chapters: ['Photosynthesis', 'Plant Parts', 'Reproduction', 'Types of Plants'],
      simulation: 'photosynthesis'
    },
    {
      id: 'cell-structure',
      title: 'Cell - The Unit of Life',
      titleHindi: 'कोशिका - जीवन की इकाई',
      icon: Microscope,
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      description: 'Zoom into cells! Animal vs Plant cells',
      hindiDesc: 'कोशिका के अंदर झांको!',
      level: 'Class 8-10',
      chapters: ['Cell Structure', 'Organelles', 'Cell Division', 'Comparing Cells'],
      simulation: 'cell'
    },
    {
      id: 'ecosystem',
      title: 'Ecosystems & Food Chain',
      titleHindi: 'पारिस्थितिकी तंत्र',
      icon: TreeDeciduous,
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      description: 'Build food chains, explore habitats!',
      hindiDesc: 'खाद्य श्रृंखला बनाओ!',
      level: 'Class 6-10',
      chapters: ['Food Chain', 'Food Web', 'Habitats', 'Conservation'],
      simulation: 'foodchain'
    }
  ];

  // Science modules for navigation
  const scienceModules = [
    { id: 'circuits', title: 'Circuit Lab', icon: Zap, route: '/science/circuits', color: '#f59e0b' },
    { id: 'biology', title: 'Biology Lab', icon: Leaf, route: '/science/biology', color: '#10b981' },
    { id: 'physics', title: 'Force & Motion', icon: Bug, route: '/science/physics', color: '#06b6d4' },
    { id: 'machines', title: 'Simple Machines', icon: Dna, route: '/science/machines', color: '#a855f7' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-emerald-50">
      {/* Header with Navigation */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/science')}
                className="p-2 hover:bg-green-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  Biology Lab
                  <Badge className="bg-green-100 text-green-700 text-xs">LIVE</Badge>
                </h1>
              </div>
            </div>
            
            {/* Module Navigation Tabs */}
            <div className="hidden lg:flex items-center gap-1 bg-gray-100 rounded-full p-1">
              {scienceModules.map((mod) => (
                <button
                  key={mod.id}
                  onClick={() => navigate(mod.route)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    mod.id === 'biology' 
                      ? 'bg-white shadow-sm text-green-700 font-medium' 
                      : 'text-gray-600 hover:bg-white/50'
                  }`}
                >
                  <mod.icon className="w-4 h-4" style={{ color: mod.color }} />
                  <span>{mod.title}</span>
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {soundEnabled ? <Volume2 className="w-5 h-5 text-gray-600" /> : <VolumeX className="w-5 h-5 text-gray-400" />}
              </button>
              <Badge className="bg-emerald-100 text-emerald-700">Class 6-10</Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700 font-medium">Interactive Biology Simulations</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Discover the <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Magic of Life</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            🔬 <span className="font-medium">Dosto!</span> Yahan hum biology ko live simulations mein seekhenge - 
            <span className="text-green-700 font-medium"> cells se lekar ecosystems tak!</span>
          </p>

          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              { icon: Microscope, label: '10+ Simulations', color: 'text-purple-600' },
              { icon: Heart, label: 'Human Body', color: 'text-red-500' },
              { icon: Leaf, label: 'Plant Life', color: 'text-green-600' },
              { icon: Dna, label: 'NCERT Aligned', color: 'text-blue-600' }
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-sm text-gray-700">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experiments Grid */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Choose Your Experiment
            <span className="text-gray-500 font-normal text-base ml-2">(अपना प्रयोग चुनो)</span>
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            {experiments.map((exp) => (
              <Card 
                key={exp.id}
                className={`relative overflow-hidden ${exp.bgColor} border-2 ${exp.borderColor} hover:shadow-xl cursor-pointer group transition-all duration-300 hover:scale-[1.02]`}
                onClick={() => setActiveExperiment(exp)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${exp.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                      <exp.icon className="w-7 h-7 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-lg font-bold text-gray-900">{exp.title}</h4>
                        <Badge variant="outline" className="text-xs">{exp.level}</Badge>
                      </div>
                      <p className="text-sm text-gray-500 mb-1">{exp.titleHindi}</p>
                      <p className="text-sm text-gray-600 mb-3">{exp.description}</p>
                      
                      {/* Chapters Preview */}
                      <div className="flex flex-wrap gap-2">
                        {exp.chapters.slice(0, 3).map((ch, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-white/60 rounded-full text-gray-600">
                            {ch}
                          </span>
                        ))}
                        {exp.chapters.length > 3 && (
                          <span className="text-xs px-2 py-1 bg-white/60 rounded-full text-gray-500">
                            +{exp.chapters.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Arrow */}
                    <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-gray-700 group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Simulation Section */}
      {activeExperiment && (
        <SimulationModal 
          experiment={activeExperiment} 
          onClose={() => setActiveExperiment(null)}
          soundEnabled={soundEnabled}
        />
      )}

      {/* Featured: Heart Animation */}
      <section className="py-12 px-4 bg-gradient-to-r from-red-50 to-pink-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Heart Animation */}
            <div className="relative">
              <HeartSimulation />
            </div>
            
            {/* Info */}
            <div>
              <Badge className="bg-red-100 text-red-700 mb-4">Featured Simulation</Badge>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Human Heart - The Pumping Machine
              </h3>
              <p className="text-gray-600 mb-4">
                🫀 Dekho kaise tumhara dil har din <span className="font-semibold text-red-600">1 lakh baar</span> dhakdhak karta hai! 
                Blood ko pure body mein pump karta hai - 24/7, bina ruke!
              </p>
              <ul className="space-y-2 mb-6">
                {['4 Chambers: 2 Atria + 2 Ventricles', 'Pumps 5 liters blood per minute', 'Connected to lungs for oxygen'].map((point, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    {point}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => setActiveExperiment(experiments[0])}
                className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Heart Simulation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured: Photosynthesis */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Info */}
            <div>
              <Badge className="bg-green-100 text-green-700 mb-4">Plant Biology</Badge>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Photosynthesis - Nature's Kitchen
              </h3>
              <p className="text-gray-600 mb-4">
                🌱 Plants apna khana khud banate hain! <span className="font-semibold text-green-600">Sunlight + Water + CO₂ = Glucose + O₂</span>
                <br/>
                Isliye plants humein oxygen dete hain!
              </p>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <p className="text-sm font-mono text-green-800">
                  6CO₂ + 6H₂O + Light → C₆H₁₂O₆ + 6O₂
                </p>
                <p className="text-xs text-green-600 mt-1">Carbon Dioxide + Water + Sunlight → Glucose + Oxygen</p>
              </div>
              <Button 
                onClick={() => setActiveExperiment(experiments[1])}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              >
                <Leaf className="w-4 h-4 mr-2" />
                Explore Photosynthesis
              </Button>
            </div>
            
            {/* Photosynthesis Animation */}
            <div className="relative">
              <PhotosynthesisSimulation />
            </div>
          </div>
        </div>
      </section>

      {/* Cell Comparison Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-purple-50 to-violet-50">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-purple-100 text-purple-700 mb-4">Cell Biology</Badge>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Animal Cell vs Plant Cell
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <CellDiagram type="animal" />
            <CellDiagram type="plant" />
          </div>
          
          <Button 
            onClick={() => setActiveExperiment(experiments[2])}
            className="mt-8 bg-gradient-to-r from-purple-500 to-violet-500"
          >
            <Microscope className="w-4 h-4 mr-2" />
            Explore Cell Structure
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            🧬 Practiq Science Lab - Making Biology Fun! | NCERT Aligned for Classes 6-10
          </p>
          <p className="text-gray-500 text-xs mt-2">
            © 2026 TaxSetu AI | Designed for Indian Students
          </p>
        </div>
      </footer>
    </div>
  );
};

// ==================== HEART SIMULATION COMPONENT ====================
const HeartSimulation = () => {
  const [isBeating, setIsBeating] = useState(true);
  const [bpm, setBpm] = useState(72);
  
  return (
    <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-red-100">
      <div className="text-center">
        {/* Animated Heart */}
        <div className="relative inline-block mb-4">
          <div className={`relative ${isBeating ? 'animate-heartbeat' : ''}`}>
            <Heart 
              className="w-32 h-32 text-red-500 fill-red-500 drop-shadow-lg" 
            />
            {/* Blood flow indicators */}
            {isBeating && (
              <>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-8 bg-gradient-to-b from-blue-500 to-transparent rounded-full animate-pulse"></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-8 bg-gradient-to-t from-red-600 to-transparent rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
              </>
            )}
          </div>
          
          {/* Pulse rings */}
          {isBeating && (
            <>
              <div className="absolute inset-0 border-4 border-red-300 rounded-full animate-ping opacity-20"></div>
              <div className="absolute inset-0 border-2 border-red-200 rounded-full animate-pulse"></div>
            </>
          )}
        </div>
        
        {/* BPM Display */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-red-500" />
          <span className="text-2xl font-bold text-gray-900">{bpm}</span>
          <span className="text-gray-500">BPM</span>
        </div>
        
        {/* Controls */}
        <div className="flex justify-center gap-3">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setIsBeating(!isBeating)}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            {isBeating ? 'Pause' : 'Start'}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setBpm(Math.min(bpm + 10, 120))}
            className="border-gray-200"
          >
            Faster
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setBpm(Math.max(bpm - 10, 40))}
            className="border-gray-200"
          >
            Slower
          </Button>
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute top-4 right-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Deoxygenated
        </span>
        <span className="flex items-center gap-1 mt-1">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          Oxygenated
        </span>
      </div>
    </div>
  );
};

// ==================== PHOTOSYNTHESIS SIMULATION ====================
const PhotosynthesisSimulation = () => {
  const [sunIntensity, setSunIntensity] = useState(80);
  const [waterLevel, setWaterLevel] = useState(70);
  const [isActive, setIsActive] = useState(true);
  
  const oxygenProduction = Math.round((sunIntensity * waterLevel) / 100);
  
  return (
    <div className="relative bg-white rounded-3xl p-6 shadow-xl border border-green-100">
      {/* Sun */}
      <div className="absolute -top-4 right-8">
        <Sun className={`w-12 h-12 text-yellow-500 ${isActive ? 'animate-spin-slow' : ''}`} style={{ opacity: sunIntensity / 100 }} />
      </div>
      
      {/* Plant */}
      <div className="relative h-48 flex items-end justify-center">
        {/* Pot */}
        <div className="absolute bottom-0 w-24 h-12 bg-gradient-to-b from-amber-600 to-amber-800 rounded-b-xl"></div>
        
        {/* Stem */}
        <div className="relative z-10 w-2 h-32 bg-gradient-to-t from-green-700 to-green-500 rounded-full mb-10">
          {/* Leaves */}
          <div className="absolute -left-8 top-4 w-10 h-6 bg-gradient-to-r from-green-600 to-green-400 rounded-full transform -rotate-45"></div>
          <div className="absolute -right-8 top-8 w-10 h-6 bg-gradient-to-l from-green-600 to-green-400 rounded-full transform rotate-45"></div>
          <div className="absolute -left-6 top-16 w-8 h-5 bg-gradient-to-r from-green-600 to-green-400 rounded-full transform -rotate-30"></div>
          
          {/* Flower */}
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <Flower2 className="w-8 h-8 text-pink-400" />
          </div>
        </div>
        
        {/* CO2 bubbles coming in */}
        {isActive && (
          <div className="absolute left-4 top-1/2">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i}
                className="absolute w-3 h-3 bg-gray-300 rounded-full animate-float-left text-[8px] flex items-center justify-center text-gray-600"
                style={{ animationDelay: `${i * 0.5}s`, left: `${i * 10}px` }}
              >
                CO₂
              </div>
            ))}
          </div>
        )}
        
        {/* O2 bubbles going out */}
        {isActive && (
          <div className="absolute right-4 top-1/3">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i}
                className="absolute w-3 h-3 bg-blue-200 rounded-full animate-float-right text-[8px] flex items-center justify-center text-blue-600"
                style={{ animationDelay: `${i * 0.5}s`, right: `${i * 10}px` }}
              >
                O₂
              </div>
            ))}
          </div>
        )}
        
        {/* Water drops */}
        {isActive && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2">
            <Droplets className="w-4 h-4 text-blue-400 animate-bounce" />
          </div>
        )}
      </div>
      
      {/* Controls */}
      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-3">
          <Sun className="w-4 h-4 text-yellow-500" />
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={sunIntensity}
            onChange={(e) => setSunIntensity(Number(e.target.value))}
            className="flex-1 h-2 bg-yellow-100 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-gray-500 w-8">{sunIntensity}%</span>
        </div>
        <div className="flex items-center gap-3">
          <Droplets className="w-4 h-4 text-blue-500" />
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={waterLevel}
            onChange={(e) => setWaterLevel(Number(e.target.value))}
            className="flex-1 h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-gray-500 w-8">{waterLevel}%</span>
        </div>
      </div>
      
      {/* Output */}
      <div className="mt-4 p-3 bg-green-50 rounded-xl text-center">
        <p className="text-sm text-green-700">
          <span className="font-bold">O₂ Production:</span> {oxygenProduction}%
        </p>
      </div>
    </div>
  );
};

// ==================== CELL DIAGRAM ====================
const CellDiagram = ({ type }) => {
  const isPlant = type === 'plant';
  
  const organelles = isPlant ? [
    { name: 'Cell Wall', color: 'bg-green-600', position: 'border' },
    { name: 'Chloroplast', color: 'bg-green-400', desc: 'For photosynthesis' },
    { name: 'Vacuole', color: 'bg-blue-200', desc: 'Large, stores water' },
    { name: 'Nucleus', color: 'bg-purple-400', desc: 'Control center' }
  ] : [
    { name: 'Cell Membrane', color: 'bg-orange-300', position: 'border' },
    { name: 'Mitochondria', color: 'bg-red-400', desc: 'Powerhouse' },
    { name: 'Nucleus', color: 'bg-purple-400', desc: 'Control center' },
    { name: 'Lysosome', color: 'bg-yellow-400', desc: 'Digestive' }
  ];

  return (
    <div className={`p-6 rounded-2xl ${isPlant ? 'bg-green-50 border-2 border-green-200' : 'bg-orange-50 border-2 border-orange-200'}`}>
      <h4 className={`text-lg font-bold mb-4 ${isPlant ? 'text-green-800' : 'text-orange-800'}`}>
        {isPlant ? '🌱 Plant Cell' : '🔴 Animal Cell'}
      </h4>
      
      {/* Cell Visualization */}
      <div className={`relative w-40 h-40 mx-auto mb-4 rounded-full ${isPlant ? 'bg-green-100 border-8 border-green-600' : 'bg-orange-100 border-4 border-orange-300'}`}>
        {/* Nucleus */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-purple-400 rounded-full border-2 border-purple-600">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-purple-700 rounded-full"></div>
        </div>
        
        {/* Organelles */}
        {isPlant ? (
          <>
            <div className="absolute top-4 left-4 w-6 h-4 bg-green-400 rounded-full" title="Chloroplast"></div>
            <div className="absolute top-6 right-6 w-5 h-3 bg-green-400 rounded-full" title="Chloroplast"></div>
            <div className="absolute bottom-8 left-8 w-5 h-3 bg-green-400 rounded-full" title="Chloroplast"></div>
            <div className="absolute bottom-4 right-4 w-16 h-10 bg-blue-200 rounded-full opacity-60" title="Vacuole"></div>
          </>
        ) : (
          <>
            <div className="absolute top-4 left-6 w-6 h-3 bg-red-400 rounded-full" title="Mitochondria"></div>
            <div className="absolute top-8 right-4 w-5 h-3 bg-red-400 rounded-full" title="Mitochondria"></div>
            <div className="absolute bottom-6 left-4 w-4 h-4 bg-yellow-400 rounded-full" title="Lysosome"></div>
            <div className="absolute bottom-4 right-8 w-5 h-3 bg-red-400 rounded-full" title="Mitochondria"></div>
          </>
        )}
      </div>
      
      {/* Legend */}
      <div className="space-y-2">
        {organelles.map((org, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className={`w-3 h-3 ${org.color} rounded-full`}></div>
            <span className="text-gray-700 font-medium">{org.name}</span>
            {org.desc && <span className="text-gray-500 text-xs">- {org.desc}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== SIMULATION MODAL ====================
const SimulationModal = ({ experiment, onClose, soundEnabled }) => {
  const [step, setStep] = useState(0);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`p-4 bg-gradient-to-r ${experiment.color} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <experiment.icon className="w-8 h-8" />
              <div>
                <h3 className="text-xl font-bold">{experiment.title}</h3>
                <p className="text-sm opacity-80">{experiment.titleHindi}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Chapters */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {experiment.chapters.map((ch, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                  step === i 
                    ? `bg-gradient-to-r ${experiment.color} text-white` 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {i + 1}. {ch}
              </button>
            ))}
          </div>
          
          {/* Simulation Area */}
          <div className="min-h-[300px] bg-gray-50 rounded-2xl p-6 flex items-center justify-center">
            {experiment.simulation === 'heart' && <HeartSimulation />}
            {experiment.simulation === 'photosynthesis' && <PhotosynthesisSimulation />}
            {experiment.simulation === 'cell' && (
              <div className="grid grid-cols-2 gap-8">
                <CellDiagram type="animal" />
                <CellDiagram type="plant" />
              </div>
            )}
            {experiment.simulation === 'foodchain' && <FoodChainSimulation />}
          </div>
          
          {/* Info Text */}
          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <p className="text-sm text-blue-800">
              💡 <strong>Fun Fact:</strong> {getFunFact(experiment.simulation, step)}
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex justify-between">
          <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          <Button 
            onClick={() => setStep(Math.min(experiment.chapters.length - 1, step + 1))}
            disabled={step === experiment.chapters.length - 1}
            className={`bg-gradient-to-r ${experiment.color}`}
          >
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== FOOD CHAIN SIMULATION ====================
const FoodChainSimulation = () => {
  const chain = [
    { name: 'Sun', icon: Sun, color: 'text-yellow-500', desc: 'Energy Source' },
    { name: 'Plant', icon: Leaf, color: 'text-green-500', desc: 'Producer' },
    { name: 'Grasshopper', icon: Bug, color: 'text-lime-600', desc: 'Primary Consumer' },
    { name: 'Frog', icon: Fish, color: 'text-emerald-500', desc: 'Secondary Consumer' },
    { name: 'Snake', icon: Bug, color: 'text-amber-700', desc: 'Tertiary Consumer' },
    { name: 'Eagle', icon: Bird, color: 'text-gray-700', desc: 'Apex Predator' }
  ];
  
  return (
    <div className="w-full">
      <h4 className="text-lg font-bold text-center mb-6">🔗 Food Chain - Energy Flow</h4>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {chain.map((item, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center p-3 bg-white rounded-xl shadow-md hover:scale-110 transition-transform cursor-pointer">
              <item.icon className={`w-10 h-10 ${item.color}`} />
              <span className="text-xs font-bold mt-1">{item.name}</span>
              <span className="text-[10px] text-gray-500">{item.desc}</span>
            </div>
            {i < chain.length - 1 && (
              <ArrowRight className="w-6 h-6 text-gray-400" />
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="text-center text-sm text-gray-600 mt-4">
        🌟 Energy flows from Sun → Producers → Consumers
      </p>
    </div>
  );
};

// Helper function for fun facts
const getFunFact = (simulation, step) => {
  const facts = {
    heart: [
      "Your heart beats about 100,000 times per day!",
      "The heart pumps about 2,000 gallons of blood daily.",
      "Your heart is about the size of your fist.",
      "The heart has its own electrical system!"
    ],
    photosynthesis: [
      "Plants produce 70% of Earth's oxygen!",
      "Chlorophyll makes plants green.",
      "Plants 'breathe in' CO2 and 'breathe out' O2.",
      "Some plants can photosynthesize underwater!"
    ],
    cell: [
      "Your body has about 37 trillion cells!",
      "The smallest cell is a red blood cell.",
      "Plant cells have cell walls, animal cells don't.",
      "Mitochondria has its own DNA!"
    ],
    foodchain: [
      "Only 10% energy transfers to the next level!",
      "Decomposers are nature's recyclers.",
      "Food webs show interconnected food chains.",
      "Removing one species affects the whole chain!"
    ]
  };
  return facts[simulation]?.[step % facts[simulation].length] || "Science is amazing!";
};

export default BiologyLabPage;

// Add these to your global CSS or tailwind config
// @keyframes heartbeat { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
// @keyframes float-left { 0% { opacity: 0; transform: translateX(20px); } 50% { opacity: 1; } 100% { opacity: 0; transform: translateX(-20px); } }
// @keyframes float-right { 0% { opacity: 0; transform: translateX(-20px); } 50% { opacity: 1; } 100% { opacity: 0; transform: translateX(20px); } }
// @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
